/**
 * The form's state: the draft, what is dirty, per-section saving, and the browser-side rescue for
 * the one field the server cannot hold half of.
 *
 * PER-SECTION SAVE, NOT ONE BIG SAVE. `updateOwnProfile` is a partial PATCH by contract, so
 * sending only the section the person just finished is both cheaper and much kinder when it is
 * refused: a 422 on the bio does not throw away the six things they typed in another section.
 *
 * THE DRAFT RESCUE, and why it is not a nicety. `bioLong` is a BAND — 600 to 1200 graphemes or
 * null, with nothing in between — so a half-written bio CANNOT be saved to the server at all. The
 * corpus already grants exactly this mitigation for the editor: the app keeps unsent text in
 * browser storage across reloads, an implementation matter and not a contract one. Without it, a
 * person who writes four hundred words, closes the tab and comes back has lost all of them, and
 * they will not come back a second time.
 *
 * The rescue is per browser, is cleared the moment the value reaches the server, and never holds
 * anything from the `private` block — a contact address and an expected rate are not text to leave
 * lying in localStorage on a shared machine.
 *
 * A SAVE'S ANSWER IS TAKEN FOR EVERY FIELD THE PERSON HAS NOT EDITED, not only for the fields it
 * sent (`adoptAnswer`, domain/form.ts): the server fills the contact e-mail itself on a first save,
 * and a form that ignored that would show an empty field and later send it back as a clear.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import type { FieldVisibility, OwnProfile, OwnProfileUpdate, Problem } from '../contract';
import { ApiError } from '../api/problem';
import {
  adoptAnswer,
  draftFromProfile,
  isUnchanged,
  toUpdateBody,
  validateDraft,
  SECTION_KEYS,
} from '../domain/form';
import type { ApplicationDraft, DraftKey, FieldErrors, SectionId } from '../domain/form';
import { useApplicationApi } from './provider';

/** Only the long-form public fields are rescued. Nothing private, ever. */
const RESCUED_KEYS = [
  'bioLong',
  'bioShort',
  'about',
  'experience',
  'approach',
  'education',
] as const;
type RescuedKey = (typeof RESCUED_KEYS)[number];

const RESCUE_STORAGE_KEY = 'moonx.apply.draft.v1';

function readRescue(): Partial<Record<RescuedKey, string>> {
  try {
    const raw = window.localStorage.getItem(RESCUE_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Partial<Record<RescuedKey, string>> = {};
    for (const key of RESCUED_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  } catch {
    // Private browsing, a full quota, a corrupt value. None of them is worth an error on screen.
    return {};
  }
}

function writeRescue(value: Partial<Record<RescuedKey, string>>): void {
  try {
    if (Object.keys(value).length === 0) window.localStorage.removeItem(RESCUE_STORAGE_KEY);
    else window.localStorage.setItem(RESCUE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Same: storage being unavailable must never stop somebody typing.
  }
}

export interface SectionSaveState {
  readonly saving: boolean;
  /**
   * True after a save the server accepted, so the section can say so. It is a flag and not a
   * timestamp on purpose: C11 bans reading the clock outside the injected Clock, and "saved" is
   * a fact about the last action rather than a moment anybody needs to see.
   */
  readonly saved: boolean;
  readonly error: unknown;
}

export interface ProfileForm {
  readonly draft: ApplicationDraft;
  readonly errors: FieldErrors;
  /** True when this key differs from what the server last returned. */
  readonly isDirty: (key: DraftKey) => boolean;
  readonly isSectionDirty: (section: SectionId) => boolean;
  readonly setField: <K extends DraftKey>(key: K, value: ApplicationDraft[K]) => void;
  readonly sectionState: (section: SectionId) => SectionSaveState;
  readonly saveSection: (section: SectionId) => Promise<boolean>;
  /**
   * The seven per-field switches SAVE THEMSELVES, and that is deliberate rather than lazy.
   *
   * `visibility` is one object covering fields that live in two different sections, so putting it
   * in either section's key list makes the other section wrong: toggle "headline is public" in
   * section 1 and section 2 would also claim unsaved changes. A switch is also a discrete
   * decision rather than text being drafted, so there is nothing to review before sending it.
   * The whole `FieldVisibility` object goes each time, which is idempotent.
   */
  readonly setVisibility: (key: keyof FieldVisibility, next: boolean) => void;
  /** True while a visibility switch is in flight. The switches stay usable (`setVisibility`). */
  readonly visibilitySaving: boolean;
  readonly visibilityError: unknown;
  /** Which long-form fields are currently held only in this browser. */
  readonly rescuedKeys: readonly RescuedKey[];
  readonly discardRescue: (key: RescuedKey) => void;
  /** True while any section is saving. */
  readonly anySaving: boolean;
}

const EMPTY_SECTION_STATE: SectionSaveState = { saving: false, saved: false, error: null };

/**
 * Pulls the field-scoped part of a 422 out of a Problem, when the server names fields.
 *
 * The contract does NOT define a per-field member on `Problem` — the schema is deliberately open
 * and `error_code` is the whole of what a client branches on. So this reads an `errors` map if one
 * is present and otherwise returns nothing, which is the honest answer: the caller then shows one
 * message at the top of the form rather than guessing which field was meant.
 */
function fieldErrorsFromProblem(problem: Problem | undefined): FieldErrors {
  if (problem === undefined) return {};
  const maybe = (problem as unknown as { errors?: unknown }).errors;
  if (typeof maybe !== 'object' || maybe === null) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(maybe as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

export interface UseProfileFormOptions {
  readonly profile: OwnProfile | undefined;
  readonly slugEditable: boolean;
  /** Called with the server's own response after a successful section save. */
  readonly onSaved: (profile: OwnProfile) => void;
}

export function useProfileForm(options: UseProfileFormOptions): ProfileForm {
  const { t } = useTranslation('application');
  const api = useApplicationApi();
  const { profile, slugEditable, onSaved } = options;

  const [baseline, setBaseline] = useState<ApplicationDraft | undefined>(undefined);
  const [draft, setDraft] = useState<ApplicationDraft | undefined>(undefined);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [sections, setSections] = useState<Partial<Record<SectionId, SectionSaveState>>>({});
  const [rescue, setRescue] = useState<Partial<Record<RescuedKey, string>>>(() => readRescue());
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityError, setVisibilityError] = useState<unknown>(null);

  const seeded = useRef(false);

  // Seed once from the server, then let the person's edits win. Re-seeding on every profile
  // change would overwrite what they are typing the moment another section saves.
  useEffect(() => {
    if (profile === undefined || seeded.current) return;
    seeded.current = true;
    const fromServer = draftFromProfile(profile);
    const rescued = readRescue();
    const merged: ApplicationDraft = { ...fromServer };
    for (const key of RESCUED_KEYS) {
      const held = rescued[key];
      // Only restore text the server does not already have. If the two agree there is nothing to
      // rescue, and if the server has something different it has been saved since and wins.
      if (held !== undefined && fromServer[key].length === 0) merged[key] = held;
    }
    setBaseline(fromServer);
    setDraft(merged);
  }, [profile]);

  const setField = useCallback<ProfileForm['setField']>((key, value) => {
    setDraft((current) => (current === undefined ? current : { ...current, [key]: value }));
    // A field the person has just edited stops carrying the server's complaint about its old value.
    setServerErrors((current) => {
      if (current[key as string] === undefined) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
    if ((RESCUED_KEYS as readonly string[]).includes(key) && typeof value === 'string') {
      setRescue((current) => {
        const next = { ...current, [key as RescuedKey]: value };
        if (value.length === 0) delete next[key as RescuedKey];
        writeRescue(next);
        return next;
      });
    }
  }, []);

  const localErrors = useMemo(
    () => (draft === undefined ? {} : validateDraft(draft, slugEditable, t)),
    [draft, slugEditable, t],
  );

  const errors = useMemo<FieldErrors>(() => {
    const merged = { ...localErrors, ...serverErrors };
    return merged.primaryLocation === PLACE_REFUSED
      ? { ...merged, primaryLocation: t(PLACE_REFUSED) }
      : merged;
  }, [localErrors, serverErrors, t]);

  const isDirty = useCallback(
    (key: DraftKey) => {
      if (draft === undefined || baseline === undefined) return false;
      return !isUnchanged(draft, baseline, [key]);
    },
    [draft, baseline],
  );

  const isSectionDirty = useCallback(
    (section: SectionId) => {
      if (draft === undefined || baseline === undefined) return false;
      return !isUnchanged(draft, baseline, SECTION_KEYS[section]);
    },
    [draft, baseline],
  );

  const sectionState = useCallback(
    (section: SectionId) => sections[section] ?? EMPTY_SECTION_STATE,
    [sections],
  );

  const saveSection = useCallback(
    async (section: SectionId): Promise<boolean> => {
      if (draft === undefined || baseline === undefined) return false;
      const keys: readonly DraftKey[] = SECTION_KEYS[section];
      const body = toUpdateBody(draft, baseline, keys);
      if (Object.keys(body).length === 0) return true;

      // Refuse locally only for the fields this save is actually sending. A bad value in another
      // section is not this button's business.
      const blocking = Object.entries(localErrors).filter(([key]) =>
        keys.some((k) => key === k || key.startsWith(`${k}.`)),
      );
      if (blocking.length > 0) return false;

      setSections((s) => ({ ...s, [section]: { saving: true, saved: false, error: null } }));
      try {
        const saved = await api.updateOwnProfile(body);
        const nextBaseline = draftFromProfile(saved);
        setBaseline(nextBaseline);
        // Keep the person's own text for anything they have edited and this save did not send, so
        // a save in one section never silently reverts an edit in another - and take the server's
        // answer for everything else, including what it filled in itself (`adoptAnswer`: the
        // contact e-mail a first save seeds, design 14).
        setDraft((current) =>
          current === undefined ? nextBaseline : adoptAnswer(current, baseline, nextBaseline, keys),
        );
        // Whatever reached the server no longer needs rescuing.
        setRescue((current) => {
          const next = { ...current };
          for (const key of RESCUED_KEYS) {
            if (keys.includes(key)) delete next[key];
          }
          writeRescue(next);
          return next;
        });
        setSections((s) => ({ ...s, [section]: { saving: false, saved: true, error: null } }));
        onSaved(saved);
        return true;
      } catch (cause) {
        if (cause instanceof ApiError && cause.status === 422) {
          setServerErrors((current) => ({
            ...current,
            ...fieldErrorsFromProblem(cause.problem),
            ...placeRefusal(cause, body),
          }));
        }
        setSections((s) => ({ ...s, [section]: { saving: false, saved: false, error: cause } }));
        return false;
      }
    },
    [api, draft, baseline, localErrors, onSaved],
  );

  /*
   * THE SWITCHES ARE NEVER DISABLED WHILE ONE SAVES - disabling all of them made every other
   * switch on the page blink at each press (the owner, 2026-10-19). What that disabling guarded
   * against is held here instead: the saves run ONE AFTER ANOTHER, each sends the switches as they
   * stand when it goes (`visibilityNow`), and only the newest save's answer is put on the page, so
   * an older answer arriving last cannot flip a newer press back.
   */
  const visibilityNow = useRef<FieldVisibility | undefined>(undefined);
  const visibilityQueue = useRef<Promise<void>>(Promise.resolve());
  const visibilityTurn = useRef(0);

  const setVisibility = useCallback(
    (key: keyof FieldVisibility, next: boolean) => {
      if (draft === undefined || baseline === undefined) return;
      const before = visibilityNow.current ?? draft.visibility;
      const merged: FieldVisibility = { ...before, [key]: next };
      visibilityNow.current = merged;
      const turn = ++visibilityTurn.current;
      // Optimistic, because a switch that lags behind the finger reads as broken. A refusal puts
      // it back where it was and says so.
      setDraft((current) => (current === undefined ? current : { ...current, visibility: merged }));
      setVisibilitySaving(true);
      setVisibilityError(null);
      visibilityQueue.current = visibilityQueue.current.then(async () => {
        const sentFrom = baseline;
        try {
          const saved = await api.updateOwnProfile({ visibility: visibilityNow.current ?? merged });
          const nextBaseline = draftFromProfile(saved);
          setBaseline(nextBaseline);
          if (turn === visibilityTurn.current) {
            // A switch can be the first save of all, and so the one whose answer carries the
            // seeded contact e-mail: the same rule as a section's save (`adoptAnswer`).
            setDraft((current) =>
              current === undefined
                ? current
                : adoptAnswer(current, sentFrom, nextBaseline, ['visibility']),
            );
            visibilityNow.current = undefined;
          }
          onSaved(saved);
        } catch (cause: unknown) {
          setVisibilityError(cause);
          if (turn === visibilityTurn.current) {
            visibilityNow.current = undefined;
            setDraft((current) =>
              current === undefined ? current : { ...current, visibility: before },
            );
          }
        } finally {
          if (turn === visibilityTurn.current) setVisibilitySaving(false);
        }
      });
    },
    [api, draft, baseline, onSaved],
  );

  const discardRescue = useCallback((key: RescuedKey) => {
    setRescue((current) => {
      const next = { ...current };
      delete next[key];
      writeRescue(next);
      return next;
    });
    setDraft((current) => (current === undefined ? current : { ...current, [key]: '' }));
  }, []);

  const rescuedKeys = useMemo(
    () =>
      RESCUED_KEYS.filter((key) => {
        const held = rescue[key];
        if (held === undefined || held.length === 0) return false;
        return baseline === undefined || baseline[key] !== held;
      }),
    [rescue, baseline],
  );

  const anySaving = Object.values(sections).some((s) => s?.saving === true);

  const emptyDraft = useMemo<ApplicationDraft>(
    () =>
      draftFromProfile({
        displayName: null,
        visibility: {},
        expertise: [],
        tags: [],
        links: [],
        signs: { birthDataOnFile: false, computedSignsPublic: false },
      }),
    [],
  );

  return {
    draft: draft ?? emptyDraft,
    errors,
    isDirty,
    isSectionDirty,
    setField,
    sectionState,
    saveSection,
    setVisibility,
    visibilitySaving,
    visibilityError,
    rescuedKeys,
    discardRescue,
    anySaving,
  };
}

/**
 * ★ A PICKED PLACE THE SERVER WILL NOT TAKE (design 25, 3.3). The pick is checked, not looked up,
 * and a zone outside the platform's IANA list answers 422 `PROFILE_LIMIT_EXCEEDED`, naming no
 * field. Every other limit in a section's save is checked on screen first, so with a pick in the
 * body that refusal is the place's, and the field says so plainly rather than the section alone.
 */
function placeRefusal(cause: ApiError, body: OwnProfileUpdate): FieldErrors {
  const named = Object.keys(fieldErrorsFromProblem(cause.problem)).length > 0;
  if (named || cause.errorCode !== 'PROFILE_LIMIT_EXCEEDED' || !body.primaryLocation) {
    return {};
  }
  return { primaryLocation: PLACE_REFUSED };
}

/** The place's refusal is held among the server's errors as its catalogue key, worded when read. */
const PLACE_REFUSED = 'form.placeRefused';
